import React, { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Table,
} from 'reactstrap';
import { FaLock, FaLockOpen, FaMinus } from 'react-icons/fa';
import i18n, { formatNumber } from '@/utils/i18n';
import { useUpdateOrderFulfillmentMutation } from '../../../../graphql/mutations/updateOrderFulfillment.generated';
import {
  ContainerTypeAmount,
  OrderFulfillmentType,
} from '../../../../graphql/generatedTypes';
import OrderFulfillmentAddContainerTypes, {
  ContainerType,
} from './OrderFulfillmentAddContainerTypes';
import ContainerTypeDisplay from '../../../../components/ContainerType/ContainerTypeDisplay';

// Component Props Interface
interface OrderFulfillmentDetailPageProps {
  isInternal: boolean;
  manualAmounts: ContainerTypeAmount[];
  fulfillmentId?: string;
  containerTypes: ContainerType[];
  setManualAmounts: React.Dispatch<React.SetStateAction<ContainerTypeAmount[]>>;
  refetch?: () => void;
  editModeAlwaysOn: boolean;
  type: OrderFulfillmentType;
}

// OrderFulfillmentDetailPage Component
const OrderFulfillmentManualAmounts: React.FC<
  OrderFulfillmentDetailPageProps
> = ({
  isInternal,
  fulfillmentId,
  manualAmounts,
  containerTypes,
  editModeAlwaysOn,
  setManualAmounts,
  refetch,
  type,
}) => {
  const [editManualAmounts, setEditManualAmounts] = useState(editModeAlwaysOn);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [manualAmountIdToRemove, setManualAmountIdToRemove] = useState<
    number | null
  >();

  const [updateOrderFulfillment, { loading: updating }] =
    useUpdateOrderFulfillmentMutation({
      refetchQueries: ['OrderFulfillment'],
    });

  function handleManualAmountsUpdate(newManualAmounts: ContainerTypeAmount[]) {
    setManualAmounts(newManualAmounts);
  }

  function handleAmountChange(e: any, index: number, sealed: boolean) {
    const newAmount = e.target.value.replaceAll(/[^0-9]/g, '');
    const newManualAmounts = [...manualAmounts];

    if (sealed) {
      newManualAmounts[index].amount_sealed = newAmount;
    } else {
      newManualAmounts[index].amount = newAmount;
    }

    handleManualAmountsUpdate(newManualAmounts);
  }

  async function onSave() {
    if (fulfillmentId == null) {
      return;
    }
    const manualAmountsToSave = manualAmounts
      .filter((manualAmount) => {
        return (
          Number(manualAmount.amount) > 0 ||
          Number(manualAmount.amount_sealed) > 0
        );
      })
      .map((manualAmount) => ({
        container_type_id: manualAmount.container_type_id,
        amount: parseInt(manualAmount.amount as any),
        amount_sealed: parseInt(manualAmount.amount_sealed as any),
      }));
    await updateOrderFulfillment({
      variables: {
        order_fulfillment_id: fulfillmentId,
        manual_amounts: manualAmountsToSave,
      },
    });

    setEditManualAmounts(false);
    refetch?.();
  }

  const isDeliveryOFType = type === OrderFulfillmentType.Delivery;
  const isReturnOFType = type === OrderFulfillmentType.Return;

  return (
    <div>
      {isInternal && editManualAmounts && (
        <>
          <OrderFulfillmentAddContainerTypes
            isDisabled={updating}
            containerTypes={containerTypes}
            orderFulfillmentType={type}
            manualAmounts={manualAmounts}
            setManualAmounts={(manualAmounts) => {
              handleManualAmountsUpdate(manualAmounts);
              setEditManualAmounts(true);
            }}
          />
        </>
      )}

      {isInternal && !editManualAmounts && !editModeAlwaysOn && (
        <Button
          className="float-end mt-2 mb-2"
          disabled={updating}
          onClick={() => setEditManualAmounts(true)}
        >
          <FaLock />
          {' ' + i18n.t('Common:edit')}
        </Button>
      )}
      {editManualAmounts && fulfillmentId != null && (
        <Button
          color="success"
          className="float-end mt-2 mb-2"
          disabled={updating}
          onClick={() => onSave()}
        >
          <FaLockOpen />
          {' ' + i18n.t('Common:save')}
        </Button>
      )}
      <Table striped hover bordered>
        <thead>
          <tr>
            <th>{i18n.t('CreateOrderFulfillment:containerType')}</th>

            {isReturnOFType && (
              <>
                <th>{i18n.t('Common:amountClean')}</th>
                <th>{i18n.t('Common:amountDirty')}</th>
              </>
            )}

            {isDeliveryOFType && <th>{i18n.t('Common:amountClean')}</th>}

            <th>{i18n.t('Common:sum')}</th>

            {isInternal && editManualAmounts && <th></th>}
          </tr>
        </thead>
        <tbody>
          {manualAmounts.map((manualAmount, index) => (
            <tr key={manualAmount.container_type_id}>
              <td className="align-middle">
                <ContainerTypeDisplay
                  containerType={containerTypes.find(
                    (ct) => ct.id === manualAmount.container_type_id,
                  )}
                />
              </td>

              {isReturnOFType && (
                <td className="numbers-formatted align-middle">
                  {editManualAmounts ? (
                    <Input
                      type="number"
                      min={0}
                      value={manualAmount.amount_sealed}
                      onChange={(e) => handleAmountChange(e, index, true)}
                      style={{ width: '100px' }}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                    />
                  ) : (
                    formatNumber(manualAmount.amount_sealed)
                  )}
                </td>
              )}

              <td className="numbers-formatted align-middle">
                {editManualAmounts ? (
                  <Input
                    type="number"
                    min={0}
                    value={manualAmount.amount}
                    onChange={(e) => handleAmountChange(e, index, false)}
                    style={{ width: '100px' }}
                    stepHoldDelay={500}
                    stepHoldInterval={100}
                  />
                ) : (
                  formatNumber(manualAmount.amount)
                )}
              </td>

              <td className="numbers-formatted align-middle">
                {formatNumber(
                  Number(manualAmount.amount) +
                    Number(manualAmount.amount_sealed),
                )}
              </td>

              {isInternal && editManualAmounts && (
                <td className="align-middle">
                  <button
                    className="btn btn-danger float-end mt-2 mb-2"
                    disabled={!editManualAmounts}
                    onClick={() => {
                      setManualAmountIdToRemove(manualAmount.container_type_id);
                      setShowRemoveModal(true);
                    }}
                  >
                    <FaMinus />
                    {' ' + i18n.t('Common:remove')}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal
        isOpen={showRemoveModal}
        toggle={() => {
          setShowRemoveModal(false);
        }}
      >
        <ModalBody>
          {i18n.t('OrderFulfillment:genericRemoveModalBody')}
          <ContainerTypeDisplay
            containerType={containerTypes.find(
              (ct) => ct.id === manualAmountIdToRemove,
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              const filteredManualAmounts = manualAmounts.filter((mA) => {
                return mA.container_type_id !== manualAmountIdToRemove;
              });

              handleManualAmountsUpdate(filteredManualAmounts);

              setManualAmountIdToRemove(null);
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('Common:yes')}
          </Button>{' '}
          <Button
            color="secondary"
            onClick={() => {
              setManualAmountIdToRemove(null);
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('Common:no')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OrderFulfillmentManualAmounts;
